import { queryOptions } from "@tanstack/react-query";
import { apiClient, unwrap } from "../../api/client";
import type { components } from "../../api/generated/vision-api";
import type { DashboardDocument } from "./copy";

export type Dashboard = components["schemas"]["Dashboard"];
/** A dashboard with the ETag it was answered with, sent back as If-Match on save (dashboard-save-conflict DSC-06). */
export type LoadedDashboard = Dashboard & { etag?: string };

/** The data of a call with the response's ETag, or ApiError as unwrap throws it. */
async function withETag<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T & { etag?: string }> {
  const result = await call;
  const data = await unwrap(Promise.resolve(result));
  return { ...data, etag: result.response.headers.get("ETag") ?? undefined };
}
export type DashboardSummary = components["schemas"]["DashboardSummary"];

/** The user's own dashboards and the organisation's shared ones, by name (WEB-DSH-VIEW-01). */
export const dashboardsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/dashboards"],
    queryFn: async () => {
      const items: DashboardSummary[] = [];
      let cursor: string | undefined;
      do {
        const page = await unwrap(apiClient("customers").GET("/v1/dashboards", { params: { query: { cursor } } }));
        items.push(...page.items);
        cursor = page.next_cursor ?? undefined;
      } while (cursor);
      return items;
    },
  });

export const dashboardQuery = (orgId: string, id: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/dashboards/{dashboard_id}", id],
    queryFn: (): Promise<LoadedDashboard> =>
      withETag(apiClient("customers").GET("/v1/dashboards/{dashboard_id}", { params: { path: { dashboard_id: id } } })),
  });

export function createDashboard(document: DashboardDocument, shared = false) {
  return unwrap(apiClient("customers").POST("/v1/dashboards", { body: { document: { ...document }, shared } }));
}

/** Saves over the version `etag` names; a newer one on the server answers 412 (DSC-03), nothing is saved. */
export function saveDashboard(id: string, document: DashboardDocument, shared: boolean, etag?: string) {
  return unwrap(
    apiClient("customers").PUT("/v1/dashboards/{dashboard_id}", {
      params: { path: { dashboard_id: id }, header: etag ? { "If-Match": etag } : undefined },
      body: { document: { ...document }, shared },
    }),
  );
}

export function deleteDashboard(id: string) {
  return unwrap(
    apiClient("customers").DELETE("/v1/dashboards/{dashboard_id}", { params: { path: { dashboard_id: id } } }),
  );
}
